import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import classNames from 'classnames';
import { ReactComponent as ChevronRight } from '@assets/images/chevron-right.svg';
import { ReactComponent as InfoIcon } from '@assets/images/information-circle.svg';
import Sider from '@components/Sider';
import { useAuthentication } from '@hooks/authentication';
import { useViewport } from '@hooks/viewport';
import { useNavigationBarStore } from '@stores/navigationBarStore';
import { isPartnerRole } from '@utils/common';
import { openLink } from '@utils/common';
import Header, { HeaderProps } from './Header';
import { useLayoutContext } from './LayoutContext';
import { computeLayout } from './layoutUtils';

interface AppLayoutProps {
  children: React.ReactNode;
  headerProps?: HeaderProps;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children, headerProps }) => {
  const location = useLocation();
  const { isMobile } = useViewport();
  const { isSiderOpen, closeSider, openSider } = useNavigationBarStore();
  const layoutCtx = useLayoutContext();

  const authentication = useAuthentication();
  const { showGuideBar, landingPage, layoutHeight } = computeLayout({
    pathname: location.pathname,
    isMobile,
    isPartner: isPartnerRole(authentication.authStore.user),
  });

  const hideFooterPage = ['chat'].includes(location.pathname.split('/')?.[1]);
  const isPartner = isPartnerRole(authentication.authStore.user);

  const hideSider = !isSiderOpen || !isPartner;

  let hideFooter = !isPartnerRole(authentication.authStore.user);
  if (landingPage) {
    hideFooter = true;
  }
  if (hideFooterPage) {
    hideFooter = true;
  }

  // const desktopNavBarHeight = 81; // kept for compatibility where used in styles

  useEffect(() => {
    if (isMobile || landingPage) {
      closeSider();
    } else {
      openSider();
    }
  }, [isMobile, landingPage, openSider, closeSider]);

  // If used inside GlobalLayout, set header props via context
  const isInsideGlobalLayout = !!layoutCtx?.layoutActive;
  useEffect(() => {
    if (!isInsideGlobalLayout) return;
    layoutCtx!.setHeaderProps(headerProps);
    return () => layoutCtx!.setHeaderProps(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInsideGlobalLayout, headerProps]);

  useEffect(() => {
    if (authentication?.authStore?.user?.id) {
      document.title = '아이고고 | 파트너센터';
    } else {
      document.title = '아이고고 | 파트너스';
    }
  }, [authentication?.authStore?.user?.id]);

  if (isInsideGlobalLayout) {
    return <>{children}</>;
  }

  return (
    <>
      <Header landing={landingPage} {...headerProps} />
      {showGuideBar && (
        <div
          style={{
            height: '56px',
            //width: '100%',
            padding: '0 20px',
            background: 'white',
            borderBottom: '1px solid #e8eaed',
          }}>
          <button
            className="link-button"
            onClick={() => {
              openLink('https://igogo.notion.site/1c796a81624b80808569e863fabfab8c?pvs=4');
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '56px' }}>
              <InfoIcon />
              <div style={{ color: '#242424', textAlign: 'right', fontSize: '16px', fontWeight: 600 }}>
                파트너스가 처음이신가요?
              </div>
              <ChevronRight />
            </div>
          </button>
        </div>
      )}
      <Layout
        style={{ height: layoutHeight }}
        className={classNames('partner-admin-app ant-layout-has-sider', !authentication?.authStore?.user && 'login')}>
        {isMobile && isSiderOpen && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 10,
            }}
            onClick={closeSider}
          />
        )}
        <Sider hide={hideSider} onHide={closeSider} />
        <Layout.Content
          id="layout-content"
          style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
          {children}
          {!hideFooter && <Layout.Footer>Copyright (주)아이들랩. All rights Reserved.</Layout.Footer>}
        </Layout.Content>
      </Layout>
    </>
  );
};

export default AppLayout;
